import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { useTheme } from "styled-components";
import "@reach/auto-id";
import { useKeyboardShortcut } from "../../hooks/use-keyboard-shortcut.es.js";
import { useHandler } from "../../hooks/use-handler.es.js";
import { useDisplayMode } from "../../hooks/use-display-mode.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "lodash/fp";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
import { Icon } from "../icon/icon.es.js";
import { UtilityButtonClickArea, UtilityButtonInner, UtilityButtonIcon, UtilityButtonText } from "./utility-button.styles.es.js";
const propTypes = {
  "aria-disabled": PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  "aria-expanded": PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  "aria-haspopup": PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  "aria-pressed": PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  as: PropTypes.elementType,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  display: PropTypes.oneOf(["block", "default"]),
  displayMode: PropTypes.oneOf(["dark", "light"]),
  dropdown: PropTypes.bool,
  expanded: PropTypes.bool,
  forwardedAs: PropTypes.elementType,
  hasPopup: PropTypes.bool,
  icon: PropTypes.node,
  inline: PropTypes.bool,
  keyboardShortcut: PropTypes.func,
  magnitude: PropTypes.oneOf(["medium", "small", "xsmall"]),
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseDown: PropTypes.func,
  pressed: PropTypes.bool,
  preventDefault: PropTypes.bool,
  stopPropagation: PropTypes.bool,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  variant: PropTypes.oneOf(["accent", "danger", "default"])
};
const defaultProps = {
  "aria-disabled": void 0,
  "aria-expanded": void 0,
  "aria-haspopup": void 0,
  "aria-pressed": void 0,
  as: void 0,
  children: void 0,
  disabled: false,
  display: "default",
  displayMode: void 0,
  dropdown: false,
  expanded: void 0,
  forwardedAs: void 0,
  hasPopup: void 0,
  icon: void 0,
  inline: false,
  keyboardShortcut: void 0,
  magnitude: "medium",
  onClick: void 0,
  onKeyDown: void 0,
  onMouseDown: void 0,
  pressed: void 0,
  preventDefault: false,
  stopPropagation: false,
  type: "button",
  variant: "default"
};
const UtilityButton = forwardRef(
  ({
    "aria-disabled": ariaDisabled,
    "aria-expanded": ariaExpanded,
    "aria-haspopup": ariaHasPopup,
    "aria-pressed": ariaPressed,
    as,
    children,
    disabled: disabledProp,
    display,
    displayMode: displayModeProp,
    dropdown,
    expanded: expandedProp,
    forwardedAs,
    hasPopup: hasPopupProp,
    icon,
    inline,
    keyboardShortcut,
    magnitude,
    onClick,
    onKeyDown,
    onMouseDown,
    pressed: pressedProp,
    preventDefault,
    stopPropagation,
    type,
    variant,
    ...restProps
  }, forwardedRef) => {
    const theme = useTheme();
    const ref = useKeyboardShortcut({
      predicate: keyboardShortcut,
      callback: () => ref.current.click()
    });
    const displayMode = useDisplayMode(displayModeProp);
    const disabled = disabledProp || String(ariaDisabled) === "true";
    const expanded = expandedProp || String(ariaExpanded) === "true";
    const hasPopup = hasPopupProp || String(ariaHasPopup) === "true";
    const pressed = pressedProp || String(ariaPressed) === "true";
    const handleClick = useHandler((event) => {
      if (stopPropagation) {
        event.stopPropagation();
      }
      if (preventDefault) {
        event.preventDefault();
      }
      if (onClick) {
        onClick(event);
      }
    });
    useImperativeHandle(forwardedRef, () => ref.current);
    return /* @__PURE__ */ jsx(
      UtilityButtonClickArea,
      {
        ref,
        as: as || forwardedAs,
        "aria-disabled": disabled || void 0,
        "aria-expanded": expanded || void 0,
        "aria-haspopup": hasPopup || void 0,
        "aria-pressed": pressed || void 0,
        onClick: !disabled ? handleClick : void 0,
        onKeyDown: !disabled ? onKeyDown : void 0,
        onMouseDown: !disabled ? onMouseDown : void 0,
        type: as === "button" || disabled ? type : void 0,
        $display: display,
        $displayMode: displayMode,
        $variant: variant,
        ...restProps,
        children: /* @__PURE__ */ jsxs(UtilityButtonInner, { magnitude, inline, children: [
          icon && /* @__PURE__ */ jsx(UtilityButtonIcon, { magnitude, children: typeof icon === "string" ? /* @__PURE__ */ jsx(Icon, { magnitude, type: icon }) : icon }),
          children && /* @__PURE__ */ jsx(UtilityButtonText, { children }),
          dropdown && /* @__PURE__ */ jsx(
            Icon,
            {
              color: theme.color.gray6,
              magnitude: "xsmall",
              transform: expanded ? "rotate(180deg)" : void 0,
              transitionDuration: 300,
              type: "down"
            }
          )
        ] })
      }
    );
  }
);
UtilityButton.propTypes = propTypes;
UtilityButton.defaultProps = defaultProps;
export {
  UtilityButton
};
